import {ParsedUrlQuery} from 'querystring'
import {uniq} from 'ramda'
import {hasSeoAttributes} from 'seo/seo-db/attributes'

import {logger} from '@vio/core/src/utils/logging/logger'

import {
  extractFilterSlugsFromTaxonomy,
  FRIENDLY_SRP_FILTER_DIVIDER,
  FRIENDLY_SRP_FILTER_URL_DELIMITER,
  FRIENDLY_SRP_PATH_NAME
} from './getFiltersFromTaxonomy'

import type {SeoBreadcrumbItem} from 'seo/getBreadcrumb'
import type {SeoAttributeConfig, SeoAttributes} from 'seo/seo-db/attributes'

const log = logger.child({
  module: 'getSeoSRPPath'
})

export interface SortedFilter extends SeoAttributeConfig {
  slug: string
}

export const getSortedFilters = (
  filterSlugs: string[] | string | undefined,
  seoAttributes: SeoAttributes
): SortedFilter[] => {
  if (!filterSlugs?.length || !hasSeoAttributes(seoAttributes)) {
    return []
  }

  const filters: SortedFilter[] = []

  for (const slug of filterSlugs) {
    const filterConfig = seoAttributes[slug]

    if (!filterConfig) {
      log.warn(`Unknown filter slug "${slug}"`, filterSlugs)
      continue
    }

    filters.push({
      ...filterConfig,
      slug
    })
  }

  if (filters.length === 0) {
    log.warn(`No filters found`, {filterSlugs})
    return []
  }

  return filters.sort((a, b) => a.priority - b.priority)
}

export const getSeoSRPPath = ({
  pathParams,
  seoAttributes,
  breadcrumb
}: {
  pathParams: ParsedUrlQuery
  seoAttributes: SeoAttributes
  breadcrumb: SeoBreadcrumbItem[]
}): string | null => {
  const {taxonomy} = pathParams

  const basePath = breadcrumb?.at(-1)?.path
  if (!basePath) {
    return null
  }

  const filterSlugs = extractFilterSlugsFromTaxonomy(taxonomy)
  const sortedFilters = getSortedFilters(filterSlugs, seoAttributes)

  return basePath + buildFilterSuffix(sortedFilters.map(filter => filter.slug))
}

interface BuildSeoSRPPathParams {
  placeId: string
  locale: string
  landmark?: string
  city?: string
  country: string
  attributes?: string[]
}

export const buildSeoSRPPath = ({
  placeId,
  locale,
  landmark,
  city,
  country,
  attributes
}: BuildSeoSRPPathParams) => {
  const path = [`/${FRIENDLY_SRP_PATH_NAME}`, placeId, locale]

  const taxonomyParts = uniq([country, city, landmark])
  taxonomyParts.forEach(part => {
    if (part) {
      path.push(part)
    }
  })

  return path.join('/') + buildFilterSuffix(attributes)
}

export const buildFilterSuffix = (attributes?: string[]): string => {
  const validAttributes = attributes?.filter(Boolean) ?? []
  if (!validAttributes.length) return ''

  return `/${FRIENDLY_SRP_FILTER_URL_DELIMITER}/${validAttributes.join(FRIENDLY_SRP_FILTER_DIVIDER)}`
}
